import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	CheckOutline,
	DuplicateOutline,
	ExclamationCircleOutline,
	Flex,
	IconButton,
	ThumbDownOutline,
	ThumbDownSolid,
	ThumbUpOutline,
	ThumbUpSolid,
	Tooltip,
	useToast,
} from '@bonusly/design-system';

export type FeedbackType = 'positive' | 'negative' | undefined;

interface BizyMessageFeedbackProps {
	messageId: string;
	content: string;
	feedbackType?: FeedbackType;
	onPositiveFeedback: (messageId: string) => void;
	onNegativeFeedback: (messageId: string) => void;
	onClearFeedback: (messageId: string) => void;
	isLatestReply?: boolean;
}

const iconButtonStyles = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

/**
 * Thumbs up/down feedback buttons and copy button for Bizy assistant messages.
 * Always visible so users know they can provide feedback.
 * Clicking a selected button again clears the feedback.
 */
export const BizyMessageFeedback: FC<BizyMessageFeedbackProps> = ({
	messageId,
	content,
	feedbackType,
	onPositiveFeedback,
	onNegativeFeedback,
	onClearFeedback,
	isLatestReply = true,
}) => {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const [copyState, setCopyState] = useState<'idle' | 'success' | 'error'>('idle');
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	const isPositiveSelected = feedbackType === 'positive';
	const isNegativeSelected = feedbackType === 'negative';

	const handleThumbsUpClick = () => {
		if (isPositiveSelected) {
			onClearFeedback(messageId);
		} else {
			onPositiveFeedback(messageId);
		}
	};

	const handleThumbsDownClick = () => {
		if (isNegativeSelected) {
			onClearFeedback(messageId);
		} else {
			onNegativeFeedback(messageId);
		}
	};

	const handleCopy = useCallback(() => {
		navigator.clipboard
			.writeText(content)
			.then(() => {
				setCopyState('success');
			})
			.catch(() => {
				setCopyState('error');
				toast({ title: t('feedback.copyFailed'), status: 'error', duration: 3000 });
			})
			.finally(() => {
				if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
				copyTimeoutRef.current = setTimeout(() => setCopyState('idle'), 3000);
			});
	}, [content, toast, t]);

	const copyIcon =
		copyState === 'success' ? (
			<CheckOutline boxSize={5} />
		) : copyState === 'error' ? (
			<ExclamationCircleOutline boxSize={5} />
		) : (
			<DuplicateOutline boxSize={5} />
		);

	return (
		<Flex mt={1} gap="4px" opacity={isLatestReply ? 1 : 0} _groupHover={isLatestReply ? undefined : { opacity: 1 }} transition="opacity 0.15s ease-in-out">
			<Tooltip label={t('feedback.copy')} placement="top">
				<IconButton
					aria-label={t('feedback.copy')}
					icon={copyIcon}
					{...iconButtonStyles}
					{...(copyState === 'error' && { color: 'error.fg' })}
					onClick={handleCopy}
				/>
			</Tooltip>

			<Tooltip label={t('feedback.thumbsUp')} placement="top">
				<IconButton
					aria-label={t('feedback.thumbsUp')}
					icon={isPositiveSelected ? <ThumbUpSolid boxSize={5} /> : <ThumbUpOutline boxSize={5} />}
					{...iconButtonStyles}
					onClick={handleThumbsUpClick}
					isDisabled={isNegativeSelected}
				/>
			</Tooltip>

			<Tooltip label={t('feedback.thumbsDown')} placement="top">
				<IconButton
					aria-label={t('feedback.thumbsDown')}
					icon={isNegativeSelected ? <ThumbDownSolid boxSize={5} /> : <ThumbDownOutline boxSize={5} />}
					{...iconButtonStyles}
					onClick={handleThumbsDownClick}
					isDisabled={isPositiveSelected}
				/>
			</Tooltip>
		</Flex>
	);
};
